import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Navigation, NavigationOff, AlertCircle } from "lucide-react";
import { useLocationSharing } from "@/App";

/**
 * LocationSharingButton - Uses global LocationSharingContext from App.js
 * 
 * The location sharing state is managed at the App level so it persists
 * across page navigation. This component just provides the UI to start/stop sharing.
 */
export default function LocationSharingButton({ 
  liveCallId, 
  className = "", 
  compact = false,
  currentSharingCallId = null, // Kept for backwards compatibility but not used
  onSharingChange = () => {} // Kept for backwards compatibility
}) {
  const locationSharing = useLocationSharing();
  const [loading, setLoading] = useState(false);
  const [permissionDenied, setPermissionDenied] = useState(false);

  // Get sharing state from context
  const isSharing = locationSharing?.isSharing || false;
  const sharingCallId = locationSharing?.sharingCallId;
  const accuracy = locationSharing?.accuracy;

  // Determine if THIS button should show as sharing
  // For Map page (liveCallId is null): show sharing if we're sharing for ANY call
  // For Response page (liveCallId is set): show sharing only if it matches our call
  const showAsSharing = isSharing && (liveCallId === null || sharingCallId === liveCallId);

  // Toggle sharing
  const toggleSharing = async () => {
    if (!locationSharing) {
      console.error('LocationSharingContext not available');
      return;
    }

    if (showAsSharing) {
      // Stop sharing
      setLoading(true);
      await locationSharing.stopSharing();
      onSharingChange(null);
      setLoading(false);
    } else {
      // Start sharing
      setLoading(true);
      const result = await locationSharing.startSharing(liveCallId);
      if (result.success) {
        onSharingChange(liveCallId);
        setPermissionDenied(false);
      } else if (result.error === 'permission_denied') {
        setPermissionDenied(true);
      }
      setLoading(false);
    }
  };

  // Get button text and style
  const getButtonProps = () => {
    if (permissionDenied) {
      return {
        text: compact ? "Denied" : "Location Denied",
        icon: <AlertCircle className="w-4 h-4" />,
        variant: "destructive",
      };
    }

    if (showAsSharing) {
      return {
        text: compact ? "Stop Share" : "Stop Sharing",
        icon: <Navigation className="w-4 h-4" />,
        variant: "default",
        className: "bg-green-600 hover:bg-green-700",
      };
    }

    return {
      text: compact ? "Share" : "Share Location",
      icon: <NavigationOff className="w-4 h-4" />,
      variant: "outline",
    };
  };

  const buttonProps = getButtonProps();

  if (compact) {
    return (
      <div className="flex items-center gap-1">
        <Button
          onClick={toggleSharing}
          disabled={loading || permissionDenied}
          className={`${buttonProps.className || ""} ${className}`}
          variant={buttonProps.variant}
          size="sm"
        >
          {loading ? (
            <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
          ) : (
            buttonProps.icon
          )}
          <span className="ml-1">{buttonProps.text}</span>
        </Button>
        {showAsSharing && accuracy !== null && (
          <Badge
            variant="outline"
            className={`text-xs ${
              accuracy < 20 ? "border-green-500 text-green-400" : ""
            }${accuracy >= 20 && accuracy < 50 ? "border-orange-500 text-orange-400" : ""}${
              accuracy >= 50 ? "border-red-500 text-red-400" : ""
            }`}
          >
            ±{accuracy}m
          </Badge>
        )}
      </div>
    );
  }

  return (
    <div className="flex items-center gap-2">
      <Button
        onClick={toggleSharing}
        disabled={loading || permissionDenied}
        className={`${buttonProps.className || ""} ${className}`}
        variant={buttonProps.variant}
      >
        {loading ? (
          <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
        ) : (
          buttonProps.icon
        )}
        <span className="ml-2">{buttonProps.text}</span>
      </Button>

      {showAsSharing && accuracy !== null && (
        <Badge
          variant="outline"
          className={`
            ${accuracy < 20 ? "border-green-500 text-green-400" : ""}
            ${accuracy >= 20 && accuracy < 50 ? "border-orange-500 text-orange-400" : ""}
            ${accuracy >= 50 ? "border-red-500 text-red-400" : ""}
          `}
        >
          ±{accuracy}m
        </Badge>
      )}
    </div>
  );
}
